import React, { useEffect, useRef, useState } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import {
  applyAction, getBotAction, getPlayerView, getValidActions,
} from '../../../games/lodden-thinks/lodden';
import type {
  LoddenState, LoddenAction, LoddenPlayer,
} from '../../../games/lodden-thinks/lodden';
import type { RoomView } from '../../../types/room';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: LoddenState | null;
  onFinish: () => void;
}

const ROLE_COLORS: Record<string, string> = {
  'lodden': '#7c3aed',
  'bettor-a': '#1d4ed8',
  'bettor-b': '#b91c1c',
  'spectator': '#6b7280',
};

const ROLE_LABEL: Record<string, string> = {
  'lodden': 'Lodden',
  'bettor-a': 'Bettor A',
  'bettor-b': 'Bettor B',
  'spectator': 'Spectator',
};

function RoleBadge({ role }: { role: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: '0.72em',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      background: ROLE_COLORS[role] ?? '#6b7280',
      color: '#fff',
      whiteSpace: 'nowrap',
    }}>
      {ROLE_LABEL[role] ?? role}
    </span>
  );
}

function PlayerRow({ player, myId }: { player: LoddenPlayer; myId: string }) {
  const isMe = player.id === myId;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
      background: isMe ? '#eff6ff' : '#f9fafb',
      border: `1px solid ${isMe ? '#bfdbfe' : '#e5e7eb'}`,
      borderRadius: 8, marginBottom: 6,
    }}>
      <RoleBadge role={player.role} />
      <span style={{ flex: 1, fontWeight: isMe ? 600 : 400, color: '#111827' }}>
        {player.name}
        {isMe && <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6, fontSize: '0.85em' }}>(you)</span>}
        {player.isBot && <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4, fontSize: '0.82em' }}>Bot</span>}
      </span>
      {player.bettorWins > 0 && (
        <span style={{ fontSize: '0.85em', color: '#374151' }}>
          {player.bettorWins} {player.bettorWins === 1 ? 'win' : 'wins'}
        </span>
      )}
    </div>
  );
}

function QuestionCard({ question }: { question: string }) {
  return (
    <div style={{
      padding: '14px 18px',
      background: '#faf5ff',
      border: '1px solid #e9d5ff',
      borderLeft: '4px solid #7c3aed',
      borderRadius: 8,
      fontStyle: 'italic',
      fontSize: '1.05em',
      color: '#4c1d95',
      marginBottom: 20,
      lineHeight: 1.5,
    }}>
      {question}
    </div>
  );
}

function Scoreboard({ players, winningScore }: { players: LoddenPlayer[]; winningScore: number }) {
  const sorted = [...players].sort((a, b) => b.bettorWins - a.bettorWins);
  const spectators = sorted.filter(p => p.spectatorCorrect > 0);
  return (
    <div>
      {sorted.map(p => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={{ minWidth: 90, fontSize: '0.9em', fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.name}
          </span>
          <div style={{ flex: 1, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', height: 10 }}>
            <div style={{
              height: '100%',
              background: ROLE_COLORS[p.role] ?? '#6b7280',
              width: `${Math.min((p.bettorWins / winningScore) * 100, 100)}%`,
              borderRadius: 4,
            }} />
          </div>
          <span style={{ minWidth: 48, textAlign: 'right', fontSize: '0.88em', color: '#374151' }}>
            {p.bettorWins} / {winningScore}
          </span>
        </div>
      ))}
      {spectators.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.82em', color: '#6b7280' }}>
          Spectator predictions: {spectators.map(p => `${p.name} (${p.spectatorCorrect})`).join(', ')}
        </div>
      )}
    </div>
  );
}

const BTN: React.CSSProperties = {
  padding: '9px 22px',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.95em',
  fontWeight: 500,
  cursor: 'pointer',
};

export default function LoddenRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<LoddenState>(slug);
  const relayStartedRef = useRef(false);
  const [questionInput, setQuestionInput] = useState('');
  const [bidInput, setBidInput] = useState('');
  const [lockInInput, setLockInInput] = useState('');

  useEffect(() => {
    if (!isLeader || !initialState || relayStartedRef.current) return;
    relayStartedRef.current = true;
    startRelay('lodden-thinks', initialState);
  }, [isLeader, initialState, startRelay]);

  // Bots only auto-fire in lock-in and bidding — humans drive all other phases.
  // If the game is all-bots (no human), all phases auto-advance.
  useEffect(() => {
    if (!isLeader || !state) return;
    const hasHuman = state.players.some(p => !p.isBot);
    if (hasHuman && state.phase !== 'lock-in' && state.phase !== 'bidding') return;
    for (const player of state.players) {
      if (!player.isBot) continue;
      if (getValidActions(state, player.id).length === 0) continue;
      const action = getBotAction(player, state);
      const next = applyAction(state, action);
      if (next !== state) { sendAction(action, next); break; }
    }
  }, [state, isLeader, sendAction]);

  if (!state) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', fontFamily: 'sans-serif' }}>
        Loading game...
      </div>
    );
  }

  const view = getPlayerView(state, myId);
  const myPlayer = view.players.find(p => p.id === myId);
  const myRole = myPlayer?.role;
  const isMyBidderTurn = state.phase === 'bidding' && myRole === state.currentBidderRole && !myPlayer?.isBot;
  const lastNumericBid = [...state.bids].reverse().find(b => b.value !== null);
  const hasPriorBid = state.bids.some(b => b.value !== null);

  const send = (action: LoddenAction) => sendAction(action, applyAction(state, action));

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
      <div>
        <div style={{ fontSize: '0.75em', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 2 }}>
          Lodden Thinks
        </div>
        <div style={{ fontSize: '1.3em', fontWeight: 700, color: '#111827' }}>Round {state.round}</div>
      </div>
      <div style={{ fontSize: '0.82em', color: '#6b7280', textAlign: 'right' }}>
        First to {state.config.winningScore} wins
      </div>
    </div>
  );

  const container = (children: React.ReactNode) => (
    <div style={{ maxWidth: 560, margin: '32px auto', padding: '0 16px', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#111827' }}>
      {header}
      {children}
    </div>
  );

  // ── SETUP ──────────────────────────────────────────────
  if (state.phase === 'setup') {
    return container(
      <div>
        <div style={{ marginBottom: 14, fontSize: '0.9em', color: '#6b7280', fontWeight: 500 }}>Roles this round</div>
        {view.players.map(p => <PlayerRow key={p.id} player={p} myId={myId} />)}
        <button
          onClick={() => send({ type: 'confirmSetup' })}
          style={{ ...BTN, marginTop: 20, background: '#1d4ed8', color: '#fff' }}
        >
          Start Round
        </button>
      </div>
    );
  }

  // ── QUESTION ────────────────────────────────────────────
  if (state.phase === 'question') {
    const loddenPlayer = view.players.find(p => p.role === 'lodden');
    return container(
      <div>
        <div style={{ marginBottom: 16, color: '#374151' }}>
          Pick a question for{' '}
          <strong style={{ color: ROLE_COLORS['lodden'] }}>{loddenPlayer?.name}</strong>.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            value={questionInput}
            onChange={e => setQuestionInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && questionInput.trim()) {
                send({ type: 'submitQuestion', playerId: myId, text: questionInput });
                setQuestionInput('');
              }
            }}
            placeholder="Custom question..."
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.95em', outline: 'none' }}
          />
          <button
            onClick={() => {
              if (!questionInput.trim()) return;
              send({ type: 'submitQuestion', playerId: myId, text: questionInput });
              setQuestionInput('');
            }}
            style={{ ...BTN, background: '#374151', color: '#fff' }}
          >
            Submit
          </button>
        </div>

        {state.questionQueue.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '0.82em', color: '#9ca3af', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Queue</div>
            {state.questionQueue.map(q => (
              <div key={q.id} style={{
                display: 'flex', gap: 8, alignItems: 'center',
                padding: '8px 12px', background: '#f9fafb',
                border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 6,
              }}>
                <span style={{ flex: 1, fontSize: '0.93em' }}>{q.text}</span>
                <button
                  onClick={() => send({ type: 'selectQuestion', questionId: q.id })}
                  style={{ ...BTN, padding: '4px 12px', background: '#1d4ed8', color: '#fff', fontSize: '0.82em' }}
                >
                  Use
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ color: '#d1d5db', fontSize: '0.85em' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>
        <button
          onClick={() => send({ type: 'drawRandomQuestion' })}
          style={{ ...BTN, width: '100%', background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}
        >
          Draw Random Question
        </button>
      </div>
    );
  }

  // ── LOCK-IN ─────────────────────────────────────────────
  if (state.phase === 'lock-in') {
    const loddenPlayer = view.players.find(p => p.role === 'lodden');
    const imLodden = myRole === 'lodden' && !myPlayer?.isBot;
    return container(
      <div>
        <QuestionCard question={view.question!} />
        {imLodden ? (
          <div>
            <div style={{ marginBottom: 12, color: '#374151', fontSize: '0.93em' }}>
              Your number is <strong>secret</strong> — other players cannot see it.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number"
                value={lockInInput}
                onChange={e => setLockInInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const n = parseInt(lockInInput, 10);
                    if (!isNaN(n)) { send({ type: 'lockIn', playerId: myId, number: n }); setLockInInput(''); }
                  }
                }}
                placeholder="Your number"
                autoFocus
                style={{ width: 110, padding: '9px 12px', border: '2px solid #7c3aed', borderRadius: 8, fontSize: '1.1em', outline: 'none', fontWeight: 600 }}
              />
              <button
                onClick={() => {
                  const n = parseInt(lockInInput, 10);
                  if (!isNaN(n)) { send({ type: 'lockIn', playerId: myId, number: n }); setLockInInput(''); }
                }}
                style={{ ...BTN, background: '#7c3aed', color: '#fff' }}
              >
                Lock In
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', background: '#faf5ff',
            border: '1px solid #e9d5ff', borderRadius: 8, color: '#7c3aed',
            fontSize: '0.93em',
          }}>
            <span style={{ fontWeight: 500 }}>
              {loddenPlayer?.name} is thinking of a number...
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── BIDDING ─────────────────────────────────────────────
  if (state.phase === 'bidding') {
    const currentBidder = view.players.find(p => p.role === state.currentBidderRole);
    const mySpectatorPred = state.spectatorPredictions.find(p => p.playerId === myId);
    const bidderColor = ROLE_COLORS[state.currentBidderRole] ?? '#1d4ed8';

    return container(
      <div>
        <QuestionCard question={view.question!} />

        {/* Line display */}
        <div style={{
          textAlign: 'center', padding: '18px 16px', marginBottom: 16,
          background: '#f9fafb', border: `2px solid ${bidderColor}22`,
          borderRadius: 12,
        }}>
          <div style={{ fontSize: '0.72em', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
            Current Line
          </div>
          <div style={{ fontSize: '2.6em', fontWeight: 800, color: '#111827', lineHeight: 1 }}>
            {lastNumericBid?.value ?? '—'}
          </div>
          <div style={{ fontSize: '0.82em', color: bidderColor, marginTop: 6, fontWeight: 500 }}>
            {ROLE_LABEL[state.currentBidderRole]}'s turn
            {!isMyBidderTurn && currentBidder && ` · waiting for ${currentBidder.name}`}
          </div>
        </div>

        {/* Bid history */}
        {state.bids.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.72em', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Bid History
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {state.bids.map((b, i) => {
                const isUnder = b.action === 'take-under';
                const label = isUnder
                  ? `${ROLE_LABEL[b.bettorRole]}: UNDER`
                  : `${ROLE_LABEL[b.bettorRole]}: ${b.value}`;
                return (
                  <React.Fragment key={i}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      background: isUnder ? '#fef9c3' : '#f3f4f6',
                      border: `1px solid ${isUnder ? '#fde047' : '#d1d5db'}`,
                      borderRadius: 20,
                      fontSize: '0.85em',
                      fontWeight: i === state.bids.length - 1 ? 600 : 400,
                      color: isUnder ? '#713f12' : '#374151',
                    }}>
                      {label}
                    </span>
                    {i < state.bids.length - 1 && (
                      <span style={{ color: '#d1d5db', fontSize: '0.8em' }}>›</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* My action: bid */}
        {isMyBidderTurn && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <input
              type="number"
              value={bidInput}
              onChange={e => setBidInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const v = parseInt(bidInput, 10);
                  if (!isNaN(v)) { send({ type: 'bid', playerId: myId, value: v }); setBidInput(''); }
                }
              }}
              placeholder={`> ${lastNumericBid?.value ?? 0}`}
              autoFocus
              style={{ width: 90, padding: '9px 12px', border: `2px solid ${bidderColor}`, borderRadius: 8, fontSize: '1.05em', outline: 'none', fontWeight: 600 }}
            />
            <button
              onClick={() => {
                const v = parseInt(bidInput, 10);
                if (!isNaN(v)) { send({ type: 'bid', playerId: myId, value: v }); setBidInput(''); }
              }}
              style={{ ...BTN, background: bidderColor, color: '#fff' }}
            >
              Bid
            </button>
            {hasPriorBid && (
              <button
                onClick={() => send({ type: 'takeUnder', playerId: myId })}
                style={{ ...BTN, background: '#fef9c3', border: '1px solid #fde047', color: '#713f12' }}
              >
                Take Under
              </button>
            )}
          </div>
        )}

        {!isMyBidderTurn && (myRole === 'bettor-a' || myRole === 'bettor-b') && (
          <div style={{ color: '#6b7280', fontSize: '0.9em', marginBottom: 16 }}>
            Waiting for {ROLE_LABEL[state.currentBidderRole]}...
          </div>
        )}

        {/* Spectator predict */}
        {myRole === 'spectator' && (
          <div style={{
            padding: '12px 14px', background: '#f9fafb',
            border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12,
          }}>
            {mySpectatorPred ? (
              <div style={{ fontSize: '0.9em', color: '#374151' }}>
                Your prediction: <strong>{mySpectatorPred.side}</strong>
              </div>
            ) : hasPriorBid ? (
              <div>
                <div style={{ fontSize: '0.85em', color: '#6b7280', marginBottom: 8 }}>Predict the outcome:</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => send({ type: 'spectatorPredict', playerId: myId, side: 'over' })}
                    style={{ ...BTN, padding: '6px 16px', background: '#1d4ed8', color: '#fff' }}
                  >
                    Over
                  </button>
                  <button
                    onClick={() => send({ type: 'spectatorPredict', playerId: myId, side: 'under' })}
                    style={{ ...BTN, padding: '6px 16px', background: '#b91c1c', color: '#fff' }}
                  >
                    Under
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.85em', color: '#9ca3af' }}>Waiting for first bid to predict...</div>
            )}
          </div>
        )}

        {state.spectatorPredictions.length > 0 && (
          <div style={{ fontSize: '0.8em', color: '#9ca3af' }}>
            Predictions:{' '}
            {state.spectatorPredictions.map(pred => {
              const name = state.players.find(p => p.id === pred.playerId)?.name ?? '';
              return `${name}: ${pred.side}`;
            }).join(' · ')}
          </div>
        )}
      </div>
    );
  }

  // ── REVEAL ──────────────────────────────────────────────
  if (state.phase === 'reveal' && view.roundResult) {
    const result = view.roundResult;
    const winnerName = view.players.find(p => p.id === result.winnerId)?.name ?? '';
    const underWins = result.underWins;

    return container(
      <div>
        <QuestionCard question={view.question!} />

        <div style={{
          padding: '22px 20px',
          background: underWins ? '#fefce8' : '#eff6ff',
          border: `2px solid ${underWins ? '#fde047' : '#93c5fd'}`,
          borderRadius: 12, textAlign: 'center', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: '0.72em', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 2 }}>Line</div>
              <div style={{ fontSize: '2.4em', fontWeight: 800, color: '#111827' }}>{result.line}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: '#d1d5db', fontSize: '1.4em', fontWeight: 300 }}>vs</div>
            <div>
              <div style={{ fontSize: '0.72em', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 2 }}>Lodden's Number</div>
              <div style={{ fontSize: '2.4em', fontWeight: 800, color: '#111827' }}>{result.loddenNumber}</div>
            </div>
          </div>
          <div style={{
            display: 'inline-block',
            padding: '6px 18px',
            background: underWins ? '#fde047' : '#3b82f6',
            borderRadius: 20,
            fontSize: '1.1em', fontWeight: 700,
            color: underWins ? '#713f12' : '#fff',
            marginBottom: 8,
          }}>
            {underWins ? 'Under wins!' : 'Over wins!'}
          </div>
          <div style={{ color: '#374151', fontSize: '0.95em' }}>
            {winnerName} ({ROLE_LABEL[result.winnerRole]}) wins the round
          </div>
          {view.winnerId && (
            <div style={{
              marginTop: 14, padding: '8px 14px',
              background: '#f0fdf4', border: '1px solid #86efac',
              borderRadius: 8, color: '#166534', fontWeight: 600, fontSize: '0.95em',
            }}>
              {view.players.find(p => p.id === view.winnerId)?.name} reaches {state.config.winningScore} wins — game over!
            </div>
          )}
        </div>

        {state.spectatorPredictions.length > 0 && (
          <div style={{ marginBottom: 16, fontSize: '0.85em', color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {state.spectatorPredictions.map(pred => {
              const name = state.players.find(p => p.id === pred.playerId)?.name ?? '';
              const correct = (pred.side === 'under') === underWins;
              return (
                <span key={pred.playerId}>
                  {name}: {pred.side}{' '}
                  <span style={{ color: correct ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                    {correct ? '✓' : '✗'}
                  </span>
                </span>
              );
            })}
          </div>
        )}

        <button
          onClick={() => send({ type: 'acknowledgeReveal' })}
          style={{ ...BTN, background: '#111827', color: '#fff' }}
        >
          Continue
        </button>
      </div>
    );
  }

  // ── ROUND OVER ──────────────────────────────────────────
  if (state.phase === 'round-over') {
    return container(
      <div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.75em', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 2 }}>
            After Round {state.round}
          </div>
          <div style={{ fontSize: '1.15em', fontWeight: 600, color: '#111827' }}>Scoreboard</div>
        </div>
        <Scoreboard players={view.players} winningScore={state.config.winningScore} />
        <button
          onClick={() => send({ type: 'nextRound' })}
          style={{ ...BTN, marginTop: 24, background: '#1d4ed8', color: '#fff' }}
        >
          Next Round
        </button>
      </div>
    );
  }

  // ── COMPLETE ────────────────────────────────────────────
  if (state.phase === 'complete') {
    const winner = view.players.find(p => p.id === state.winnerId);
    return container(
      <div>
        <div style={{
          textAlign: 'center', padding: '28px 20px',
          background: '#f0fdf4', border: '2px solid #86efac',
          borderRadius: 12, marginBottom: 24,
        }}>
          <div style={{ fontSize: '1.6em', fontWeight: 800, color: '#111827', marginBottom: 4 }}>
            {winner?.name} wins!
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.9em' }}>
            Reached {state.config.winningScore} bettor wins
          </div>
        </div>
        <Scoreboard players={view.players} winningScore={state.config.winningScore} />
        {isLeader && (
          <button
            onClick={onFinish}
            style={{ ...BTN, marginTop: 24, background: '#111827', color: '#fff' }}
          >
            Play Again
          </button>
        )}
      </div>
    );
  }

  return null;
}
